import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import Text from "ol/style/Text";
import CircleStyle from "ol/style/Circle";
import GeoJSON from "ol/format/GeoJSON";
import { transform } from "ol/proj";

import { extent } from "d3-array";
import { scaleLinear } from "d3-scale";

import moment from "moment-timezone";

import Legend from "../Legend";
import Qty, { unitName } from "../UnitConversions";

import { expected_gaps } from "../Utilities/fixedDataIntervalHelpers";

const latlon = "EPSG:4326";

export default class NysmMapSet {
  static _domain = "api.nysmesonet.org";
  static _cached_mapset_times = {};
  static _requests_in_progress = {};
  static _realtime_info = {};

  constructor(dataset, mapset) {
    this._dataset = dataset;
    this._mapset = mapset;
    this._mapids = [];
    if (!NysmMapSet._cached_mapset_times.hasOwnProperty(dataset)) {
      NysmMapSet._cached_mapset_times[dataset] = {};
      NysmMapSet._requests_in_progress[dataset] = {};
      NysmMapSet._realtime_info[dataset] = {};
    }
    if (!NysmMapSet._cached_mapset_times[dataset].hasOwnProperty(mapset)) {
      NysmMapSet._cached_mapset_times[dataset][mapset] = {};
      NysmMapSet._requests_in_progress[dataset][mapset] = {};
      NysmMapSet._realtime_info[dataset][mapset] = {
        polling: false,
        watchers: [],
        timerId: null,
        lastPoll: null,
        lastAnnounced: null,
      };
    }
    this._mapset_times = NysmMapSet._cached_mapset_times[dataset][mapset];
    this._mapset_requests = NysmMapSet._requests_in_progress[dataset][mapset];
    this._mapset_realtime = NysmMapSet._realtime_info[dataset][mapset];
    this._cached_geojson = null;
    this._data_interval = moment.duration(5, "minutes");
    this._realtime_lag = moment.duration(10, "minutes");
    this._realtime_poll_interval = moment.duration(30, "seconds");
  }

  // Populates the list of map IDs available in the map set.
  async populate_mapids(date = null) {
    let response = await this.get_metadata(date);
    this._mapids = Object.keys(response.map_info);
    return this._mapids;
  }

  // Gets the metadata for a mapset at a given time. Pass null to indicate most recent available.
  async get_metadata(date = null, force = false) {
    let url =
      "//" +
      NysmMapSet._domain +
      "/data/index.php/maps/get/" +
      this._dataset +
      "/" +
      this._mapset;
    if (date) {
      let query_date = moment.isMoment(date) ? date : moment.tz(date, "UTC");
      let unix = query_date.unix();
      if (this._mapset_times.hasOwnProperty(unix)) {
        return this._mapset_times[unix];
      }
      url += "/info/" + query_date.toISOString();
    } else {
      url += "/info/latest";
    }

    if (!this._mapset_requests.hasOwnProperty(url)) {
      const getmeta = async (url) => {
        const response = await fetch(url);
        const jsonResponse = await response.json();
        const first_map = Object.keys(jsonResponse.map_info)[0];
        this._mapset_times[
          moment.tz(jsonResponse.map_info[first_map].map_time, "UTC").unix()
        ] = jsonResponse;
        return jsonResponse;
      };
      this._mapset_requests[url] = getmeta(url);
    }
    const meta = await this._mapset_requests[url];
    delete this._mapset_requests[url];
    return meta;
  }

  // gets a list of times available within a range
  async get_times(start_date = null, end_date = null) {
    let start =
      start_date === null
        ? null
        : moment.isMoment(start_date)
        ? start_date
        : moment.tz(start_date, "UTC");
    let end =
      end_date === null
        ? null
        : moment.isMoment(end_date)
        ? end_date
        : moment.tz(end_date, "UTC");
    let url =
      "//" +
      NysmMapSet._domain +
      "/data/index.php/maps/list_times/" +
      this._dataset +
      "/" +
      this._mapset +
      "/time/" +
      (start === null ? "latest" : start.toISOString());
    if (start !== null) {
      url = url + "/" + (end === null ? "latest" : end.toISOString());
    }
    let response = await fetch(url);
    let jsonResponse = await response.json();
    return jsonResponse;
  }

  // gets metadata within a range; result is the same as calling get_metadata repeatedly with times from get_times, except it's combined into a single request.
  // Pass null for the times (or omit the times) for latest record.
  async get_metadata_in_time_range(start_date = null, end_date = null) {
    let start =
      start_date === null
        ? null
        : moment.isMoment(start_date)
        ? start_date
        : moment.tz(start_date, "UTC");
    let end =
      end_date === null
        ? null
        : moment.isMoment(end_date)
        ? end_date
        : moment.tz(end_date, "UTC");

    // Find gaps in range
    const times = Object.keys(this._mapset_times)
      .sort()
      .map((t) => moment.unix(t));
    const gaps = expected_gaps(times, {
      start: start,
      end: end,
      data_interval: this._data_interval,
    });

    // For each gap, issue a request
    const now = moment();
    const awaits = [];
    gaps.forEach((g) => {
      // If the gap includes realtime data, apply a rate limit
      // or skip it entirely if realtime polling is active.
      if (g[1] > now - this._realtime_lag) {
        if (
          this._mapset_realtime.polling ||
          (this._mapset_realtime.lastPoll !== null &&
            this._mapset_realtime.lastPoll > now - this._realtime_poll_interval)
        ) {
          // Only skip the "realtime part" of the gap.
          const modified_end = g[1].clone().subtract(this._data_interval);
          if (g[0].isAfter(modified_end)) {
            // Skip request
            return;
          }
          // else, submit modified request
          awaits.push(this._query_metadata(g[0], modified_end));
          return;
        }
      }
      awaits.push(this._query_metadata(g[0], g[1]));
    });

    // Wait for all requests to resolve.
    for (let i = 0; i < awaits.length; i++) {
      try {
        await awaits[i];
      } catch (err) {
        console.error(err);
      }
    }

    // Return metadata from cache (populated by requests)
    const keys = Object.keys(this._mapset_times).sort();
    const meta = {};
    if (start === null) {
      const key = keys[keys.length - 1];
      meta[key] = this._mapset_times[key];
    } else if (end === null) {
      const start_unix = start.unix();
      keys
        .filter((k) => k >= start_unix)
        .forEach((k) => (meta[k] = this._mapset_times[k]));
    } else {
      const start_unix = start.unix();
      const end_unix = end.unix();
      keys
        .filter((k) => k >= start_unix && k <= end_unix)
        .forEach((k) => (meta[k] = this._mapset_times[k]));
    }

    return meta;
  }

  async get_geojson() {
    if (!this._cached_geojson) {
      const dataset = this._dataset;
      this._cached_geojson = (async function () {
        let nysm_meta_resp = await fetch(
          "//api.nysmesonet.org/meta/geoinfo/index.php/geojson/" + dataset
        );
        let geojson = await nysm_meta_resp.json();
        return geojson;
      })();
    }
    return await this._cached_geojson;
  }

  async make_gradient_layer(mapid, map_proj, date = null, layer_opts = {}) {
    let meta = await this.get_metadata(date);
    if (!meta.map_info.hasOwnProperty(mapid)) {
      return null;
    }
    let map_info = meta.map_info[mapid];
    let map_time = moment.tz(map_info.map_time, "UTC");
    let extent = map_info.map_extent;
    let url =
      "//" +
      NysmMapSet._domain +
      "/data/index.php/maps/get/" +
      this._dataset +
      "/" +
      this._mapset +
      "/" +
      mapid +
      "/" +
      map_time.toISOString();
    let extentArray = transform(
      [extent.left, extent.bottom],
      latlon,
      map_proj
    ).concat(transform([extent.right, extent.top], latlon, map_proj));
    let layer = new ImageLayer({
      source: new ImageStatic({
        url: url,
        crossOrigin: "anonymous",
        imageExtent: extentArray,
      }),
      title: layer_opts.title ? layer_opts.title : "Sites",
      time: map_time,
      visible: false,
    });
    const legend = this.make_gradient_legend(map_info, layer_opts);
    layer.setProperties({ imageExtent: extentArray, legend: legend });
    return layer;
  }

  // Realtime tracker should be global/static
  // Add realtime tracker
  // Remove realtime tracker
  // Store callbacks
  // Stop tracking realtime if trackers.length === 0
  add_realtime_tracker = (callbackFn) => {
    if (this._mapset_realtime.watchers.indexOf(callbackFn) < 0) {
      this._mapset_realtime.watchers.push(callbackFn);
    } else {
      console.warn("Tracking requested, but watcher already registered.");
    }
    this._check_trackers();
  };

  remove_realtime_tracker = (callbackFn) => {
    const index = this._mapset_realtime.watchers.indexOf(callbackFn);
    if (index < 0) {
      console.warn(
        "Remove tracking requested, but watcher was not in the list."
      );
    } else {
      this._mapset_realtime.watchers.splice(index, 1);
    }
    this._check_trackers();
  };

  _check_trackers = () => {
    if (
      this._mapset_realtime.watchers.length > 0 &&
      !this._mapset_realtime.polling
    ) {
      this._mapset_realtime.polling = true;
      this._schedule_poll(true);
    }
    if (
      this._mapset_realtime.watchers.length === 0 &&
      this._mapset_realtime.polling
    ) {
      this._mapset_realtime.polling = false;
      this._stop_polling();
    }
  };

  _schedule_poll = (force = false) => {
    const poll = () => {
      const now = moment();
      this._mapset_realtime.timerId = null;
      let times = Object.keys(this._mapset_times)
        .sort()
        .map((t) => moment.unix(t));
      let do_poll =
        expected_gaps(times, {
          start: null,
          end: null,
          data_interval: this._data_interval,
        }).length !== 0;
      do_poll &=
        this._mapset_realtime === null ||
        this._mapset_realtime.lastPoll <= now - this._realtime_poll_interval;
      do_poll |= force;
      let wait = !do_poll
        ? new Promise((res) => res(null))
        : this.get_times(null) // poll for latest
            .then((response) => {
              if (response.times && response.times.length === 1) {
                this._mapset_realtime.lastPoll = now;
                // response as expected
                const latest = response.times[0];
                if (!this._mapset_times.hasOwnProperty(latest)) {
                  // This is a new time! YAY!
                  const unix = moment.unix(latest);
                  this.get_metadata(unix).then((response) => {
                    this._notify_watchers(unix, response);
                    return response;
                  });
                } else {
                  // Nothing new
                  return null;
                }
              } else {
                // Unexpected response
                console.warn(
                  "Asked for latest and got something I didn't understand."
                );
                return null;
              }
            });
      wait
        .then(() => {
          // Schedule the next poll
          if (this._mapset_realtime.polling) {
            this._schedule_poll();
          }
        })
        .catch((e) => {
          console.warn("Caught exception", e);
          if (this._mapset_realtime.polling) {
            this._schedule_poll();
          }
        });
    };

    if (force) {
      poll();
    }

    if (this._mapset_realtime.timerId) {
      clearTimeout(this._mapset_realtime.timerId);
      this._mapset_realtime.timerId = null;
    }

    this._mapset_realtime.timerId = setTimeout(
      poll,
      this._realtime_poll_interval
    );
  };

  _stop_polling = () => {
    this._mapset_realtime.polling = false;
    if (this._mapset_realtime.timerId) {
      clearTimeout(this._mapset_realtime.timerId);
    }
    this._mapset_realtime.timerId = null;
  };

  // start_date and end_date are null or moment instances
  async _query_metadata(start, end) {
    let url =
      "//" +
      NysmMapSet._domain +
      "/data/index.php/maps/list_times/" +
      this._dataset +
      "/" +
      this._mapset +
      "/meta/" +
      (start === null ? "latest" : start.toISOString());
    if (start !== null) {
      url = url + "/" + (end === null ? "latest" : end.toISOString());
    }

    if (!this._mapset_requests.hasOwnProperty(url)) {
      const getmeta = async (url) => {
        const response = await fetch(url);
        const jsonResponse = await response.json();
        let notify_watchers = false;
        const newest_recvd = moment.unix(
          Object.keys(jsonResponse.meta).sort().reverse()[0]
        );
        if (this._mapset_realtime.polling) {
          notify_watchers =
            this._mapset_realtime.lastAnnounced === null ||
            newest_recvd > this._mapset_realtime.lastAnnounced;
        }
        Object.assign(this._mapset_times, jsonResponse.meta);
        if (notify_watchers) {
          this._notify_watchers(newest_recvd, this._mapset_times[newest_recvd]);
        }
        return jsonResponse.meta;
      };
      this._mapset_requests[url] = getmeta(url);
    }
    const meta = await this._mapset_requests[url];
    delete this._mapset_requests[url];
    // If it was a call within the realtime lag time, set lastPoll
    const now = moment();
    if (end === null || end > now - this._realtime_lag) {
      this._mapset_realtime.lastPoll = now;
    }
    return meta;
  }

  _notify_watchers(time, metadata) {
    this._mapset_realtime.lastAnnounced = time;
    this._mapset_realtime.watchers.forEach((v) =>
      setTimeout(() => v(time, metadata), 0)
    );
  }

  make_gradient_legend(mapid_info, layer_opts = {}) {
    const draw = (target) => {
      if (target.canvas) {
        return;
      }
      if (
        mapid_info === null ||
        !mapid_info.hasOwnProperty("layers") ||
        !mapid_info.layers.hasOwnProperty("main-gradient")
      ) {
        return;
      }
      const cbar = mapid_info.layers["main-gradient"].colorbar;
      if (!cbar) {
        return;
      }

      // calculate size
      const width = parseInt(window.getComputedStyle(target.container).width);
      const bar_height = 15;
      const text_adjustment = -1;
      const units_text_adjustment = 3;
      const text_height = 12;
      const overflow_arrow_width = bar_height / 2;
      const height =
        bar_height + text_adjustment + units_text_adjustment + text_height * 2;
      const min_width_per_label = 50;

      let converter = (x) => x;
      const srcUnits = mapid_info.layers["main-gradient"].contour_units;
      let displayUnits = srcUnits;
      if (srcUnits && layer_opts.units) {
        converter = (x) => Qty(x, srcUnits).to(layer_opts.units).scalar;
        displayUnits = layer_opts.units;
      }
      displayUnits = unitName(displayUnits);

      // Get list of labels to draw
      let labels = cbar.filter((v, i) => typeof v != "string");
      if (width / labels.length < min_width_per_label) {
        const scale = scaleLinear().domain(extent(labels)).range([0, width]);
        labels = scale.ticks(width / min_width_per_label);
      }
      const convertedLabels = labels.map(converter);
      const [min, max] = extent(convertedLabels);
      const thing = Math.floor(Math.log10(max - min) - 1);
      const places = thing >= 0 ? 0 : -thing;
      const showLabels = convertedLabels.map((x) => x.toFixed(places));

      // create canvas
      let canvas = document.createElement("canvas");
      target.canvas = canvas;
      const dpr = window.devicePixelRatio || 1;
      canvas.setAttribute("height", height * dpr);
      canvas.setAttribute("width", width * dpr);
      canvas.style.height = `${height}px`;
      canvas.style.width = `${width}px`;
      target.container.appendChild(canvas);
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.font = text_height.toString() + "px Lato";

      // determine drawing bounds
      let left_margin = ctx.measureText(showLabels[0]).width / 2;
      if (typeof cbar[0] === "string" && left_margin < overflow_arrow_width) {
        // overflow color on bottom
        left_margin = overflow_arrow_width;
      }
      let right_margin =
        ctx.measureText(showLabels[labels.length - 1]).width / 2;
      if (
        typeof cbar[cbar.length - 1] == "string" &&
        right_margin < overflow_arrow_width
      ) {
        // overflow color on top
        right_margin = overflow_arrow_width;
      }
      const legend_width = width - left_margin - right_margin;

      const xs = scaleLinear()
        .domain(
          extent(labels, function (d) {
            return d;
          })
        )
        .range([left_margin, legend_width + left_margin]);

      const min_tick_dist = 5;
      let last_plotted = null;
      const last_label = labels[labels.length - 1];
      const last_label_width = ctx.measureText(last_label).width;
      const last_label_left_edge = xs(last_label) - last_label_width / 2;

      cbar.forEach(function (v, i) {
        if (typeof v === "string" && i === 0) {
          // bottom overflow color
          ctx.fillStyle = v;
          ctx.beginPath();
          ctx.moveTo(left_margin, 0);
          ctx.lineTo(left_margin, bar_height);
          ctx.lineTo(left_margin - overflow_arrow_width, bar_height / 2);
          ctx.closePath();
          ctx.fill();
        } else if (typeof v === "string" && i === cbar.length - 1) {
          // top overflow color
          ctx.fillStyle = v;
          ctx.beginPath();
          ctx.moveTo(legend_width + left_margin, 0);
          ctx.lineTo(legend_width + left_margin, bar_height);
          ctx.lineTo(
            legend_width + left_margin + overflow_arrow_width,
            bar_height / 2
          );
          ctx.closePath();
          ctx.fill();
        } else if (typeof v === "string") {
          // color
          const x = xs(cbar[i - 1]);
          const y = 0;
          // this_width: floating point x's cause thin white lines to show between
          // boxes, so cheat by actually overlapping the boxes.
          const this_width = legend_width - x + left_margin;
          ctx.beginPath();
          ctx.fillStyle = v;
          ctx.rect(x, y, this_width, bar_height);
          ctx.fill();
        }
      });
      labels.forEach(function (v, i) {
        const show = showLabels[i];
        let to_plot = i === 0 || i === labels.length - 1; // Always show first and last
        const value_width = ctx.measureText(show).width;
        const position = xs(v);
        const left_edge = position - value_width / 2;
        const right_edge = position + value_width / 2;
        if (last_plotted) {
          to_plot |=
            left_edge - last_plotted.right_edge > min_tick_dist &&
            last_label_left_edge - right_edge > min_tick_dist;
        }
        if (to_plot) {
          ctx.fillStyle = "black";
          ctx.textAlign = "center";
          ctx.fillText(
            show,
            position,
            bar_height + text_adjustment + text_height
          );
          last_plotted = {
            right_edge: right_edge,
            value: show,
          };
        }
      });
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(
        displayUnits,
        width / 2,
        bar_height + text_adjustment + text_height * 2
      );
    };

    const remove = (target) => {
      if (target.canvas) {
        target.container.removeChild(target.canvas);
      }
      delete target.canvas;
    };

    return new Legend({ draw, remove });
  }

  async make_obs_layer(mapid, map_proj, date = null, layer_opts = {}) {
    let meta = await this.get_metadata(date);
    if (!meta.map_info.hasOwnProperty(mapid)) {
      return null;
    }
    let map_info = meta.map_info[mapid];
    if (!map_info.layers || !map_info.layers["main-gradient"]) {
      return null;
    }
    let dataspec = map_info.layers["main-gradient"].data;
    let map_time = moment.tz(map_info.map_time, "UTC");
    let dataset_geojson = await this.get_geojson();
    let data_source = meta.sources[dataspec.source];
    if (!data_source.data_vars.hasOwnProperty(dataspec.variable)) {
      return null;
    }
    let data = data_source.data_vars[dataspec.variable];
    let index_name = data.dims[0];
    let index = data_source.coords[index_name].data;
    let scale = data.attrs.scale_factor;
    if (typeof scale === "undefined") {
      scale = 1;
    }
    let fixed_places = layer_opts.precision;
    if (typeof fixed_places === "undefined") {
      let scale_string = scale.toString();
      fixed_places =
        scale_string.indexOf(".") >= 0
          ? scale_string.length - (scale_string.indexOf(".") + 1)
          : 0;
    }
    let converter = (x) => x;
    if (layer_opts.units) {
      const srcUnits = data.attrs.units;
      if (srcUnits) {
        converter = (x) => Qty(x, srcUnits).to(layer_opts.units).scalar;
      }
    }
    let geojson = new GeoJSON({
      dataProjection: latlon,
      featureProjection: map_proj,
    });
    let layer = new VectorLayer({
      title: layer_opts.title ? layer_opts.title : "Sites",
      source: new VectorSource({
        features: geojson.readFeatures(dataset_geojson),
      }),
      visible: true,
      time: map_time,
      style: function (feature, resolution) {
        let stid = feature.getProperties().stid;
        let position = index.indexOf(stid);
        let styles = [];
        if (position >= 0 && data.data[position] !== null) {
          styles.push(
            new Style({
              text: new Text({
                font: layer_opts.font ? layer_opts.font : "14px sans-serif",
                fill: layer_opts.fill
                  ? layer_opts.fill
                  : new Fill({ color: "white" }),
                stroke: layer_opts.stroke
                  ? layer_opts.stroke
                  : new Stroke({ color: "#000000", width: 2 }),
                text: converter(data.data[position] * scale).toFixed(
                  fixed_places
                ),
                scale: 1,
              }),
            })
          );
        }
        if (position >= 0 && layer_opts.hotspot) {
          const radius = layer_opts.hotspot.radius
            ? layer_opts.hotspot.radius
            : 10;
          styles.push(
            new Style({
              image: new CircleStyle({
                radius: radius,
                // stroke: new Stroke({color: 'black', width: 1}),
                fill: new Fill({ color: [0, 0, 0, 0.0] }),
              }),
            })
          );
        }
        return styles;
      },
    });
    return layer;
  }

  get dataset() {
    return this._dataset;
  }

  get mapids() {
    return this._mapids;
  }

  get data_interval() {
    return this._data_interval.clone();
  }

  get realtime_lag() {
    return this._realtime_lag.clone();
  }
}
